import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Sparkline } from '@/components/charts/Sparkline';
import { cn } from './cn';

/*
  Stat — плитка метрики (контракт из методички dataviz): подпись · значение · единица · дельта · тренд.
  Значение — гротеск полужирный с пропорциональными цифрами (табличные — только в колонках).
  Дельта окрашена по смыслу: направление × «рост — это хорошо?» (goodDirection).
*/

interface StatProps {
  label: string;
  value: string;
  unit?: string;
  delta?: { value: number; label?: string; goodDirection?: 'up' | 'down' };
  trend?: number[];
  trendTone?: 'muted' | 'brand' | 'danger' | 'success';
  /**
   * Где живёт след истории: 'inline' — мини-график справа от числа,
   * 'background' — призрачная подложка во всю плитку, проявляется при наведении (R25).
   */
  trendFill?: 'inline' | 'background';
  size?: 'md' | 'lg';
  footer?: ReactNode;
  className?: string;
}

export function Stat({
  label,
  value,
  unit,
  delta,
  trend,
  trendTone = 'muted',
  trendFill = 'inline',
  size = 'md',
  footer,
  className,
}: StatProps) {
  const good = delta ? (delta.goodDirection === 'down' ? delta.value <= 0 : delta.value >= 0) : true;
  return (
    <div
      data-slot="stat"
      className={cn(
        'group/stat relative isolate flex flex-col gap-2 overflow-hidden rounded-lg border border-line bg-card p-4',
        className,
      )}
    >
      {trend && trendFill === 'background' && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 -bottom-px -z-10 h-14 opacity-60 transition-opacity duration-smooth group-hover/stat:opacity-100"
        >
          <Sparkline values={trend} tone={trendTone} fluid endDot={false} />
        </span>
      )}
      <div className="text-xs font-medium text-fg-muted">{label}</div>
      <div className="flex items-end justify-between gap-3">
        <div className="flex items-baseline gap-1">
          <span
            className={cn('font-semibold tracking-tight text-fg', size === 'lg' ? 'text-3xl' : 'text-2xl')}
          >
            {value}
          </span>
          {unit && <span className="text-sm text-fg-muted">{unit}</span>}
        </div>
        {trend && trendFill === 'inline' && <Sparkline values={trend} tone={trendTone} />}
      </div>
      {(delta || footer) && (
        <div className="flex items-center gap-2 text-xs">
          {delta && (
            <span
              className={cn(
                'inline-flex items-center gap-0.5 font-medium tabular-nums',
                delta.value === 0 ? 'text-fg-subtle' : good ? 'text-success-fg' : 'text-danger-fg',
              )}
            >
              {delta.value > 0 ? (
                <ArrowUpRight size={14} />
              ) : delta.value < 0 ? (
                <ArrowDownRight size={14} />
              ) : null}
              {delta.value > 0 ? '+' : ''}
              {delta.value}
              {unit === '%' ? ' п.п.' : unit ? ` ${unit}` : ''}
            </span>
          )}
          {delta?.label && <span className="text-fg-subtle">{delta.label}</span>}
          {footer}
        </div>
      )}
    </div>
  );
}
